import { useState } from 'react';
import type { StorefrontSection } from '@mlm/shared';
import { useCatalog, useFeaturedPackages, useTenantConfig } from '../../lib/hooks';
import { ProductCard } from '../product-card';

type ProductFeedConfig = Extract<StorefrontSection, { type: 'productFeed' }>['config'];

// Static cols→class lookups; cols=6 default reproduces the former home grid.
const COLS_CLASS: Record<NonNullable<ProductFeedConfig['cols']>, string> = {
  3: 'grid-cols-2 lg:grid-cols-3',
  4: 'grid-cols-2 lg:grid-cols-4',
  6: 'grid-cols-3 lg:grid-cols-6',
};

/**
 * "Just For You" infinite product grid (reads useCatalog). Extracted verbatim
 * from home.tsx (LOAD MORE adds 12 at a time).
 *
 * Can OPTIONALLY leave out anything already headlined higher up the page, so a
 * product isn't listed twice. Two independent tenant toggles, BOTH default off
 * (an un-configured tenant lists the whole visible catalog, as it always has):
 *   - `productFeedHideFeaturedPackages` — drop the curated Featured Packages.
 *   - `productFeedHideFeaturedProducts` — drop `products.featured` products.
 *
 * Opt-in rather than automatic because `products.featured` has only ever been an
 * ORDER BY boost, so tenants tick it liberally — one live client has it on 25 of
 * 28 products, and excluding by default would have emptied their home page.
 *
 * The packages toggle is self-guarding on top of that: `useFeaturedPackages()
 * .enabled` is already `data.enabled && products.length > 0`, so a disabled or
 * un-curated strip excludes nothing even when the toggle is on. TanStack Query
 * dedupes the request with FeaturedPackagesSection's, so the hook costs no extra
 * fetch.
 *
 * The filter is HERE and not in `useCatalog` on purpose — Shop, search, the
 * official store, and product recommendations all share that hook, and a hidden
 * product must remain findable there. This is page composition, not visibility.
 */
export function ProductFeedSection({ section }: { section: StorefrontSection }) {
  const cfg = section.config as ProductFeedConfig;
  const title = cfg.title ?? 'Just For You';
  const cols = cfg.cols ?? 6;
  const catalog = useCatalog();
  const featured = useFeaturedPackages();
  const tenant = useTenantConfig();
  const [shown, setShown] = useState(cfg.pageSize ?? 18);

  const hidePackages = tenant.data?.productFeedHideFeaturedPackages === true;
  const hideFeatured = tenant.data?.productFeedHideFeaturedProducts === true;
  // Both lists are small (catalog ≤200, curated ≤ a handful) and this component
  // re-renders only when a query settles or LOAD MORE is clicked — no memo needed.
  const curatedIds = new Set(
    hidePackages && featured.enabled ? featured.products.map((p) => p.id) : [],
  );
  const jfy = catalog.products.filter(
    (p) => !(hideFeatured && p.featured) && !curatedIds.has(p.id),
  );
  // Hold the loading state until every source settles, so an already-rendered
  // grid never flashes a product that a toggle is about to remove.
  const isLoading = catalog.isLoading || featured.isLoading || tenant.isLoading;

  return (
    <section className="mt-4">
      <div className="rounded-t-[4px] border-b-[3px] border-o bg-white p-4 text-center text-[21px] font-extrabold text-o">
        {title}
      </div>
      {jfy.length === 0 ? (
        <div className="rounded-b-[4px] bg-white py-16 text-center text-sm text-[#999]">
          {isLoading ? 'Loading products…' : 'No products available yet.'}
        </div>
      ) : (
        <>
          <div className={`mt-2 grid gap-2 ${COLS_CLASS[cols]}`}>
            {jfy.slice(0, shown).map((p, i) => (
              <ProductCard key={p.id} p={p} i={i} mall={p.sellerId === ''} />
            ))}
          </div>
          {shown < jfy.length && (
            <button
              onClick={() => setShown((n) => n + 12)}
              className="mx-auto mt-[18px] block w-[240px] rounded-[3px] border border-[#ddd] bg-white py-[11px] text-sm font-semibold text-[#555] transition-colors hover:border-o hover:text-o"
            >
              LOAD MORE
            </button>
          )}
        </>
      )}
    </section>
  );
}
